import { useEffect, useState } from "react";
import { MapPin, Tv } from "lucide-react";
import { SectionHeading, Reveal } from "@/components/Section";
import api, { fmtDateCT, fmtTimeCT } from "@/lib/api";

export default function MetroplexGames() {
  const [games, setGames] = useState(null);

  useEffect(() => {
    let mounted = true;
    api
      .get("/metroplex")
      .then(({ data }) => {
        if (mounted) setGames(data.games || []);
      })
      .catch(() => {
        if (mounted) setGames([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (!games || !games.length) return null;

  return (
    <section id="metroplex" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-12" data-testid="metroplex-section">
      <SectionHeading index="05" eyebrow="NEUTRAL-SITE · BOWLS · CLASSICS" title="Around the Metroplex" />
      <Reveal className="space-y-3">
        {games.map((g) => {
          const away = g.competitors.find((c) => !c.home) || g.competitors[0] || {};
          const home = g.competitors.find((c) => c.home) || g.competitors[1] || {};
          return (
            <div
              key={g.id}
              data-testid={`metroplex-game-${g.id}`}
              className="flex flex-col gap-3 rounded-xl border border-bline bg-surface p-4 transition-colors duration-200 hover:border-volt/40 sm:flex-row sm:items-center"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="flex -space-x-2">
                  {[away, home].map((c, i) =>
                    c.logo ? (
                      <img key={i} src={c.logo} alt={c.abbr || ""} className="h-9 w-9 rounded-full border border-bline bg-ink object-contain p-0.5" />
                    ) : (
                      <span key={i} className="flex h-9 w-9 items-center justify-center rounded-full border border-bline bg-ink font-score text-[9px] text-slate-400">
                        {c.abbr || "?"}
                      </span>
                    )
                  )}
                </span>
                <span className="min-w-0">
                  {g.note && <span className="block font-score text-[10px] tracking-[0.2em] text-volt">{g.note.toUpperCase()}</span>}
                  <span className="block truncate font-display text-lg tracking-wide text-white">{g.name}</span>
                  <span className="block font-score text-[10px] tracking-widest text-slate-500">
                    {g.league}
                    {g.neutral ? " · NEUTRAL SITE" : ""}
                  </span>
                </span>
              </div>
              <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end sm:gap-1">
                <span className="font-score text-xs text-slate-300">
                  {fmtDateCT(g.date)} · {g.state === "post" ? "FINAL" : fmtTimeCT(g.date)}
                </span>
                <span className="inline-flex items-center gap-1 font-score text-[10px] tracking-wider text-slate-500">
                  <MapPin className="h-3 w-3 text-volt" />
                  {g.venue}
                  {g.city ? ` · ${g.city}` : ""}
                </span>
                {g.broadcast && (
                  <span className="inline-flex items-center gap-1 font-score text-[10px] tracking-wider text-slate-500">
                    <Tv className="h-3 w-3 text-bcast" />
                    {g.broadcast}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </Reveal>
    </section>
  );
}
